import { Table, Badge } from 'react-bootstrap';
import dayjs from 'dayjs';

// Everything a repair would touch, listed rather than summarised.
//
// The drum length is the part people see; it is not the part that breaks. One
// length reaches the token, the task, the task's material, the parent drum's
// consumption, the job's material summary, the phase and job progress, and two
// Mongo collections the dashboards aggregate. A preview that says "1 drum
// changes" while eight numbers move underneath it is not a preview, it is a
// reassurance — so this shows every one of them, before and after, and refuses
// to be tidy about it.
const fmt = v => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v ?? '-');
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
};
const stamp = ts => (ts ? dayjs(Number(ts)).format('DD MMM HH:mm') : '-');

// `tokens.K-x.producedLength` reads as machinery; "drum · produced" reads as a
// thing on the floor. The path is kept alongside, because when a number looks
// wrong the next question is always which field it was.
const FIELD_LABEL = {
  producedLength: 'produced',
  availableLength: 'available',
  consumedLength: 'drawn by later phases',
  executedLength: 'executed',
  progress: 'progress'
};

function readable(path) {
  const parts = String(path).split('.');
  const head = parts[0];
  const tail = parts[parts.length - 1];

  if (head === 'tokens') return { what: 'drum', field: FIELD_LABEL[tail] || tail };
  if (head === 'tasks') {
    if (path.includes('materialExecution')) {
      const material = /\[(.+?)\]/.exec(path)?.[1] || 'material';
      return { what: 'material', field: `${material} · kg used` };
    }
    return { what: 'task', field: FIELD_LABEL[tail] || tail };
  }
  return { what: head, field: FIELD_LABEL[tail] || tail };
}

const ImpactPreview = ({ row }) => {
  if (!row) return null;

  const derived = Array.isArray(row.derived) ? row.derived : [];
  const ops = row.operators;
  const windowMoved =
    row.window && row.windowWas &&
    (row.window.from !== row.windowWas.from || row.window.to !== row.windowWas.to);

  return (
    <div className="fs-10">
      <div className="fw-semibold text-900 mb-2">
        What changes on {row.productLabel || row.tokenId}
        <span className="text-700 fw-normal ms-2 font-monospace">{row.tokenId}</span>
      </div>

      <Table size="sm" className="fs-10 mb-2 bg-transparent" style={{ maxWidth: 760 }}>
        <thead>
          <tr className="text-700">
            <th className="fw-medium">Where</th>
            <th className="fw-medium">What</th>
            <th className="fw-medium text-end">Now</th>
            <th className="fw-medium text-end">Becomes</th>
          </tr>
        </thead>
        <tbody>
          {derived.map((c, i) => {
            const r = readable(c.field);
            return (
              <tr key={`${c.field}-${i}`}>
                <td className="text-800">{r.what}</td>
                <td className="text-800" title={c.field}>{r.field}</td>
                <td className="font-monospace text-end text-700">{fmt(c.from)}</td>
                <td className="font-monospace text-end fw-semibold">{fmt(c.to)}</td>
              </tr>
            );
          })}

          {windowMoved && (
            <tr>
              <td className="text-800">window</td>
              <td className="text-800">when the drum ran</td>
              <td className="font-monospace text-end text-700">
                {stamp(row.windowWas.from)} – {stamp(row.windowWas.to)}
              </td>
              <td className="font-monospace text-end fw-semibold">
                {stamp(row.window.from)} – {stamp(row.window.to)}
              </td>
            </tr>
          )}

          {ops && ops.metresBefore !== ops.metresAfter && (
            <tr>
              <td className="text-800">operators</td>
              <td className="text-800">
                {(ops.after || []).map(o => o.operator || 'unassigned').join(', ') || '—'}
              </td>
              <td className="font-monospace text-end text-700">{fmt(ops.metresBefore)} m</td>
              <td className="font-monospace text-end fw-semibold">{fmt(ops.metresAfter)} m</td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex align-items-center gap-2 flex-wrap text-700">
        {row.records && (
          <>
            <Badge bg="secondary" className="fs-10">
              {row.records.executionLogs} execution record(s)
            </Badge>
            <Badge bg="secondary" className="fs-10">
              {row.records.productionMetrics} operator metric(s)
            </Badge>
          </>
        )}
        <span>
          — rewritten too, which is what the dashboards and Operator Performance
          read.
        </span>
      </div>

      {!derived.length && (
        <div className="text-700">
          Nothing else derives from this one — only the drum&apos;s own length moves.
        </div>
      )}
    </div>
  );
};

export default ImpactPreview;
